'use client';

import React, { useEffect, useRef } from 'react';

/** Altura reservada para o SiteHeader (sticky top:0) ao rolar ate uma secao. */
export const HEADER_OFFSET = 96;

export interface TocItem {
  id: string;
  texto: string;
  nivel: 2 | 3;
}

// Re-exportado por compatibilidade (CaseConteudo.tsx e outros client
// components ja importavam slugify daqui) - a implementacao em si vive em
// src/lib/slugify.ts, que NAO e "use client", porque um Server Component
// nao pode chamar uma funcao exportada de um modulo client (ver o
// comentario la para o erro real que isso causava em producao).
export { slugify } from '../lib/slugify';

/**
 * Sumario lateral com scrollspy e barra de progresso de leitura, compartilhado
 * entre a pagina de artigo (`/artigos/[slug]`) e a de cases (`/cases`) - as duas
 * usam o mesmo layout de leitura. O rotulo vem por prop porque cada pagina tem
 * o seu no messages/*.json ("Neste artigo" x "Neste case").
 */
export default function ArtigoToc({
  itens,
  ativoId,
  titulo,
  ariaLabel,
}: {
  itens: TocItem[];
  ativoId: string | null;
  titulo: string;
  ariaLabel: string;
}) {
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || !ativoId) return;
    const ativo = nav.querySelector<HTMLAnchorElement>(`a[href="#${CSS.escape(ativoId)}"]`);
    if (!ativo) return;
    const topo = ativo.offsetTop - nav.offsetTop;
    if (topo < nav.scrollTop || topo > nav.scrollTop + nav.clientHeight - ativo.offsetHeight) {
      nav.scrollTop = topo - nav.clientHeight / 2;
    }
  }, [ativoId]);

  if (itens.length < 2) return null;

  const progresso = (() => {
    const idx = itens.findIndex((i) => i.id === ativoId);
    return idx < 0 ? 0 : ((idx + 1) / itens.length) * 100;
  })();

  return (
    <div
      className="bg-white"
      style={{ border: '1px solid #e5e5e5', borderRadius: '8px', padding: '20px' }}
    >
      <div
        style={{
          position: 'relative',
          height: '3px',
          marginBottom: '16px',
          borderRadius: '999px',
          background: '#e5e5e5',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: '0 auto 0 0',
            width: `${progresso}%`,
            borderRadius: 'inherit',
            background: '#1a9fff',
            transition: 'width 120ms linear',
          }}
        />
      </div>
      <h2 className="font-semibold mb-3" style={{ fontSize: '15px' }}>
        {titulo}
      </h2>
      <nav
        ref={navRef}
        aria-label={ariaLabel}
        style={{ display: 'grid', gap: '2px', maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}
      >
        {itens.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            onClick={(ev) => {
              const alvo = document.getElementById(item.id);
              if (!alvo) return;
              ev.preventDefault();
              // scrollIntoView() rola o ancestral scrollavel mais proximo, e
              // este <a> vive dentro de um <nav> com overflow-y:auto - o que
              // fazia o sumario rolar sozinho em vez da pagina. Calcular a
              // posicao absoluta e rolar a window explicitamente evita isso.
              const topo = alvo.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
              window.scrollTo({ top: topo, behavior: 'smooth' });
              window.history.pushState(null, '', `#${item.id}`);
            }}
            style={{
              display: 'block',
              padding: item.nivel === 3 ? '7px 0 7px 28px' : '7px 0 7px 14px',
              // Item ativo usa #0a5fb8 (nao a cor da marca #1a9fff) para
              // atingir contraste AA (6.3:1 vs 2.8:1) - aqui a cor carrega
              // informacao funcional (secao sendo lida), diferente do uso
              // decorativo da marca em elementos como o eyebrow.
              borderLeft: `2px solid ${item.id === ativoId ? '#0a5fb8' : '#e5e5e5'}`,
              fontSize: item.nivel === 3 ? '12.5px' : '13.5px',
              lineHeight: 1.4,
              color: item.id === ativoId ? '#0a5fb8' : '#555',
              fontWeight: item.id === ativoId ? 600 : 400,
              background: item.id === ativoId ? 'rgba(10,95,184,0.08)' : 'transparent',
              textDecoration: 'none',
              overflowWrap: 'break-word',
            }}
          >
            {item.texto}
          </a>
        ))}
      </nav>
    </div>
  );
}
